/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { PrimeReactProvider } from "primereact/api";
import themeDarker from "primereact/resources/themes/mdc-dark-indigo/theme.css"; // theme
import themeLight from "primereact/resources/themes/mdc-light-indigo/theme.css"; // theme
import "components/es";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logo-dark.svg";
import brandDark from "assets/images/logo-light.svg";

import "primeflex/primeflex.css";
import "primeicons/primeicons.css";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const [permissions, setPermissions] = useState([]);
  const role = localStorage.getItem("roleId");

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    // Obtener permisos del usuario desde localStorage
    const storedPermissions = localStorage.getItem("permissions");
    if (storedPermissions) {
      setPermissions(JSON.parse(storedPermissions));
    }

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Validar si el usuario tiene permisos para acceder a una ruta
  const hasPermissionForRoute = (route) => {
    const exceptionRoutes = ["/auth/sign-in", "/auth/sign-up"]; // Ejemplo de rutas que no requieren permisos

    // Si la ruta es `/Bienvenida`, verifica si el usuario tiene permisos específicos para acceder
    // if (route.route === "/Bienvenida") {
    //  return permissions.some((permission) => permission.section === "Bienvenida") || role == 1; // Asegúrate de reemplazar "welcome" con la clave de permiso adecuada
    //}

    // Si la ruta está en el array de excepciones, retorna true
    if (exceptionRoutes.includes(route.route) || role == 1) {
      return true;
    }

    if (!route || !route.section) {
      return false;
    }
    // Compara la clave de la ruta con la sección del permiso
    return permissions.some((permission) => permission.section === route.section);
  };

  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      let isAuthenticated = localStorage.getItem("token") || "";
      if (route.route) {
        if (route.private && !isAuthenticated) {
          // Redirige al usuario a la página de inicio de sesión si intenta acceder a una ruta privada sin autenticación o sin permisos
          return <Route exact path="*" element={<Navigate to="auth/sign-in" />} key={route.key} />;
        } else if (!route.private && isAuthenticated && route.route === "/auth/sign-in") {
          // Si el usuario ya inició sesión y trata de acceder a la página de inicio de sesión, redirige a la página de bienvenida
          return <Route exact path="*" element={<Navigate to="/Bienvenida" />} key={route.key} />;
        } else {
          return <Route exact path={route.route} element={route.component} key={route.key} />;
        }
      }
      return [];

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <PrimeReactProvider>
      <CacheProvider value={rtlCache}>
        <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
          <CssBaseline />
          {layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName=""
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator />
              {configsButton}
            </>
          )}
          {layout === "vr" && <Configurator />}
          <Routes>
            {getRoutes(routes)}
            <Route path="*" element={<Navigate to="auth/sign-in" />} />
          </Routes>
        </ThemeProvider>
      </CacheProvider>
    </PrimeReactProvider>
  ) : (
    <PrimeReactProvider>
      <ThemeProvider theme={darkMode ? themeDarker && themeDark : themeLight && theme}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName=""
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          {/* <Route path="*" element={<Navigate to="/auth/sign-in" />} /> */}
        </Routes>
      </ThemeProvider>
    </PrimeReactProvider>
  );
}
